import { ModalBaseDirective } from '$core/base/_modal.component';
import { CustomValidators } from '$core/helpers/custom-validators';
import { focusFirstInvalidField } from '$core/helpers/focus-first-invaild-field';
import { handleFormHttpError } from '$core/helpers/server-errors-form-handler';
import { triggerFormValidation } from '$core/helpers/trigger-form-validation';
import { UserTypes, UserTypesMap, UserWithToken } from '$core/models/user';
import { AuthenticationService } from '$core/services/auth.service';
import { SessionService } from '$core/services/session.service';
import {
  RegistrationDataIndividual,
  RegistrationDataLegals,
  SmsCheckData,
  UserService,
} from '$core/services/user.service';
import { SwalService } from '$core/shell/swal/swal.service';
import { CaptchaComponent } from '$shared/components/captcha/captcha.component';
import { validateUnp } from '$shared/validators/unp.validator';
import { transition, trigger, useAnimation } from '@angular/animations';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { fadeIn } from 'ng-animate';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { first } from 'rxjs/operators';
import { AuthParamsValues } from '$core/models/auth-modals-query-params';
import { LoginModalComponent } from '$core/modal/login-modal/login-modal.component';
import { AuthModalsService } from '$core/services/auth-modals.service';

@Component({
  selector: 'app-registration-modal',
  templateUrl: './registration-modal.component.html',
  styleUrls: ['./registration-modal.component.scss'],
  animations: [
    trigger('parent', [transition(':enter', [])]),
    trigger('fadeInOut', [
      transition(':enter', useAnimation(fadeIn), {
        params: { timing: 0.2 },
      }),
    ]),
  ],
})
export class RegistrationModalComponent
  extends ModalBaseDirective
  implements OnInit
{
  public get phone(): string {
    return this._phone;
  }

  public set phone(v: string) {
    this._phone = v ? v.replace(/\D/g, '') : '';
  }

  public get activeType() {
    return this._activeType;
  }

  public set activeType(value) {
    if (this.form) {
      switch (value) {
        case UserTypes.Individual:
          this.form.get('phone').enable();
          this.form.get('captcha').enable();
          this.form.get('trn').disable();
          this.form.get('email').setValidators([Validators.email]);
          break;

        case UserTypes.Legal:
          this.form.get('phone').reset();
          this.form.get('phone').disable();
          this.form.get('captcha').reset();
          this.form.get('captcha').disable();
          this.form.get('trn').enable();
          this.form
            .get('email')
            .setValidators([Validators.required, Validators.email]);

          break;
      }

      this.form.get('email').updateValueAndValidity();
    }

    this._activeType = value;
  }

  public get formValue() {
    return this.form.value;
  }

  public get captchaData() {
    return this.captchaComponentInstance && this.captchaComponentInstance.data;
  }

  public readonly UserTypes = UserTypes;
  public form: FormGroup;
  @ViewChild('formEl', { static: false })
  public formEl: ElementRef<HTMLFormElement>;
  @ViewChild('formCode', { static: false }) public formCode: NgForm;
  @ViewChild('formCodeEl', { static: false })
  public formCodeEl: ElementRef<HTMLFormElement>;
  @ViewChild(CaptchaComponent, { static: false })
  public captchaComponentInstance: CaptchaComponent;
  public code: string;
  public types = [UserTypes.Individual, UserTypes.Legal];
  public typesMap = UserTypesMap;
  public isResendingCode = false;
  public isAwaitingSmsCheck = false;
  public count: number;

  private _phone: string;

  private _activeType = this.types[0];

  constructor(
    protected modalRef: BsModalRef,
    protected modalService: BsModalService,
    protected translateService: TranslateService,
    private authService: AuthenticationService,
    private sessionService: SessionService,
    private userService: UserService,
    private swalService: SwalService,
    private router: Router,
    private fb: FormBuilder,
    private authModalsService: AuthModalsService
  ) {
    super(modalRef, modalService, translateService);
  }

  public ngOnInit(): void {
    this.form = this.fb.group({
      phone: [
        { value: null, disabled: this.activeType === UserTypes.Legal },
        [Validators.required],
      ],
      trn: [
        { value: null, disabled: this.activeType === UserTypes.Individual },
        [
          Validators.required,
          Validators.minLength(9),
          Validators.maxLength(9),
          validateUnp(),
        ],
      ],
      filial: [],
      email: [
        null,
        this.activeType === UserTypes.Legal
          ? [Validators.required, Validators.email]
          : [Validators.email],
      ],
      terms_of_use: [null, [Validators.requiredTrue]],
      is_accept_advertisement: [false],
      password: [
        null,
        Validators.compose([
          Validators.required,
          CustomValidators.patternValidator(/\d/, { hasNumber: true }),
          CustomValidators.patternValidator(/[A-ZА-Я]/, {
            hasCapitalCase: true,
          }),
          CustomValidators.patternValidator(/[a-zа-я]/, {
            hasSmallCase: true,
          }),
          CustomValidators.passwordLengthValidator(),
        ]),
      ],
      captcha: [
        { value: null, disabled: this.activeType === UserTypes.Legal },
        [Validators.required],
      ],
    });
  }

  public async sendRegistration() {
    const form = this.form;
    const formEl = this.formEl;

    if (this.isLoading) return;

    if (form.invalid) {
      triggerFormValidation(form);
      focusFirstInvalidField(formEl);
      return;
    }

    const body = this.generateBody();
    this.isLoading = true;
    try {
      if (this.activeType === UserTypes.Individual) {
        await this.checkCredentials();
      }
      if (this.activeType === UserTypes.Legal) {
        await this.userService
          .registerLegals(body as RegistrationDataLegals)
          .toPromise();
        this.afterSuccessRegistration();
      }
    } catch (error) {
      const res: HttpErrorResponse = error;
      if (res.status === 422) {
        if (
          res.error.errors.phone &&
          res.error.errors.phone.includes('phone_not_verified')
        ) {
          await this.checkCredentials();
        } else {
          handleFormHttpError(res, form);
          focusFirstInvalidField(formEl);
        }
      } else {
        throw res;
      }
    } finally {
      this.isLoading = false;
    }
  }

  public async sendCode() {
    const form = this.formCode;
    const formEl = this.formCodeEl;

    if (this.isLoading) return;

    if (form.invalid) {
      focusFirstInvalidField(formEl);
      return;
    }

    const body: SmsCheckData = {
      phone: this.phone,
      code: this.code,
    };
    this.isLoading = true;
    try {
      const result = await this.userService.checkSms(body).toPromise();
      if (result.status === 'ok') {
        await this.finishRegistration().then((res) => {
          this.afterSuccessRegistration(res);
        });
      }
    } catch (error) {
      this.handleError(error as HttpErrorResponse, form, formEl);
    } finally {
      this.isLoading = false;
    }
  }

  public async resendCode() {
    const form = this.formCode;
    const formEl = this.formCodeEl;

    if (this.isResendingCode) return;

    this.isResendingCode = true;
    try {
      await this.checkCredentials();
    } catch (error) {
      this.handleError(error as HttpErrorResponse, form, formEl);
    } finally {
      this.isResendingCode = false;
    }
  }

  public toLogin() {
    this.hide();
    this.authModalsService.show(
      LoginModalComponent,
      this.activeType,
      AuthParamsValues.SIGNIN
    );
  }

  public changeTab(type: UserTypes) {
    this.authModalsService.setUserTypeQueryParam(type);
    this.activeType = type;
  }

  public hide(): void {
    this.authModalsService.hide();
    super.hide();
  }

  protected navigateToUserAgreement(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    const urlTree = this.router.createUrlTree([
      '/articles/portal-public-offer',
    ]);
    const url = this.router.serializeUrl(urlTree);

    window.open(url, '_blank');
  }

  private async checkCredentials() {
    try {
      const result = await this.userService
        .credentialCheck(this.generateBody() as RegistrationDataIndividual)
        .toPromise();
      switch (result.status) {
        case 'sent':
        case 'ok':
          this.isAwaitingSmsCheck = true;
          if (result.code) {
            this.code = result.code;
          }
          break;
        case 'registered':
          this.form.controls.phone.setErrors({ alreadyUsed: true });
          focusFirstInvalidField(this.formEl);
          break;
        default:
          throw new Error();
      }
      return result;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 422) {
          if (this.captchaComponentInstance) {
            const isCaptchaError = !!(
              error &&
              error.error &&
              error.error.errors &&
              Object.keys(error.error.errors).length === 1 &&
              error.error.errors.captcha
            );

            this.captchaComponentInstance.reload(isCaptchaError);
          }
        }
      }

      throw error;
    }
  }

  private afterSuccessRegistration(res?: UserWithToken) {
    this.hide();
    this.modalService.onHidden.pipe(first()).subscribe(() => {
      const swal = this.swalService.swal;
      if (this.activeType === UserTypes.Individual) {
        swal.title = this.translate('REGISTRATION_SUCCESSFULL');
        swal.text = this.translate('REGISTRATION_COMPLETE');
        swal.icon = 'success';
        swal.confirmButtonText = this.translate('ENTER_PERSONAL_AREA');
        swal.fire().then((e) => {
          if (e.value === true && res.token) {
            this.router.navigateByUrl('/personal');
          }
        });
        this.sessionService.session = res;
        this.authService.update(res.token, true);
      }
      if (this.activeType === UserTypes.Legal) {
        swal.title = this.translate('REGISTRATION_SUCCESSFULL');
        swal.text = this.translate('WAIT_FOR_APPROVE');
        swal.icon = 'info';
        swal.fire();
      }
    });
  }

  private async finishRegistration() {
    const body = this.generateBody();
    try {
      return await this.userService
        .registerIndividual(body as RegistrationDataIndividual)
        .toPromise();
    } catch (error) {
      throw error;
    }
  }

  private generateBody() {
    let body: RegistrationDataIndividual | RegistrationDataLegals;
    if (this.activeType === UserTypes.Individual) {
      body = {
        phone: this.phone,
        email: this.formValue.email,
        password: this.formValue.password,
        password_confirmation: this.formValue.password,
        captcha: this.formValue.captcha,
        is_accept_terms: this.formValue.terms_of_use,
        is_accept_advertisement: this.formValue.is_accept_advertisement,
        key: this.captchaData ? this.captchaData.key : null,
      } as RegistrationDataIndividual;
    }
    if (this.activeType === UserTypes.Legal) {
      body = {
        trn: this.formValue.trn,
        email: this.formValue.email,
        password: this.formValue.password,
        password_confirmation: this.formValue.password,
        is_accept_terms: this.formValue.terms_of_use,
        is_accept_advertisement: this.formValue.is_accept_advertisement,
        filial: this.formValue.filial || null,
      } as RegistrationDataLegals;
    }

    return body;
  }
}
